import {IconButton} from '@dropbox/dig-components/buttons';
import {Tabs} from '@dropbox/dig-components/tabs';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronLeftLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {LaptopMini} from '@dropbox/dig-illustrations';
import {EmptyState} from 'components/DSYS/EmptyState';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router';

import {useScorecards, useScorecardSnapshots} from './hooks';
import {
  buildScorecardSectionsFromApi,
  buildScorecardTabParam,
  formatEndOfQuarterLabel,
  formatQuarterLabel,
  formatSnapshotLabel,
  getScorecardSections,
  parseApiDate,
  parseScorecardTabParam,
} from './scorecardData';
import {ScorecardQuarterCloseSelector} from './ScorecardQuarterCloseSelector';
import {ScorecardSnapshotSummaryEditor} from './ScorecardSnapshotSummaryEditor';
import {ScorecardsTable} from './ScorecardsTable';
import {ScorecardWorkstreamUpdateCards} from './ScorecardWorkstreamUpdateCards';

const SCORECARD_CURRENT_SNAPSHOT_DT = 'current';
const SHOW_SCORECARD_SNAPSHOT_SUMMARIES = false;

const isScorecardSnapshotDateParam = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

export const Scorecards = () => {
  useDocumentTitle(t('scorecards'));

  const navigate = useNavigate();
  const {date: routeSnapshotDate} = useParams<{date?: string}>();
  const [searchParams, setSearchParams] = useSearchParams();

  const {data: snapshotsResponse} = useScorecardSnapshots();
  const snapshots = useMemo(
    () => snapshotsResponse?.snapshots ?? [],
    [snapshotsResponse?.snapshots]
  );
  const endOfQuarterSnapshots = useMemo(
    () => snapshotsResponse?.end_of_quarter_snapshots ?? [],
    [snapshotsResponse?.end_of_quarter_snapshots]
  );
  const snapshotOptions = useMemo(() => [SCORECARD_CURRENT_SNAPSHOT_DT, ...snapshots], [snapshots]);

  const routeSnapshotDateIsValid =
    routeSnapshotDate == null || isScorecardSnapshotDateParam(routeSnapshotDate);
  const selectedSnapshotDt =
    routeSnapshotDate && routeSnapshotDateIsValid
      ? routeSnapshotDate
      : SCORECARD_CURRENT_SNAPSHOT_DT;
  const snapshotIndex = snapshotOptions.indexOf(selectedSnapshotDt);

  const getScorecardSnapshotPath = useCallback(
    (snapshotDt: string) => {
      const pathname =
        snapshotDt === SCORECARD_CURRENT_SNAPSHOT_DT
          ? ROUTE_PATHS.SCORECARDS
          : ROUTE_PATHS.SCORECARDS_DATE.replace(':date', encodeURIComponent(snapshotDt));
      const search = searchParams.toString();
      return search ? `${pathname}?${search}` : pathname;
    },
    [searchParams]
  );

  useEffect(() => {
    if (!routeSnapshotDate) {
      return;
    }
    if (!routeSnapshotDateIsValid) {
      navigate(getScorecardSnapshotPath(SCORECARD_CURRENT_SNAPSHOT_DT), {replace: true});
      return;
    }
    if (snapshotsResponse && !snapshots.includes(routeSnapshotDate)) {
      navigate(getScorecardSnapshotPath(SCORECARD_CURRENT_SNAPSHOT_DT), {replace: true});
    }
  }, [
    getScorecardSnapshotPath,
    navigate,
    routeSnapshotDate,
    routeSnapshotDateIsValid,
    snapshots,
    snapshotsResponse,
  ]);

  const handleSnapshotNavigation = useCallback(
    (nextSnapshotIndex: number) => {
      const nextSnapshotDt = snapshotOptions[nextSnapshotIndex];
      if (!nextSnapshotDt) {
        return;
      }
      navigate(getScorecardSnapshotPath(nextSnapshotDt));
    },
    [getScorecardSnapshotPath, navigate, snapshotOptions]
  );

  const {data: scorecardResponse, isLoading, isError, error} = useScorecards(selectedSnapshotDt);
  const isEndOfQuarter =
    scorecardResponse?.is_end_of_quarter ?? endOfQuarterSnapshots.includes(selectedSnapshotDt);

  const scorecardTabs = useMemo(
    () => scorecardResponse?.sections ?? [],
    [scorecardResponse?.sections]
  );

  const requestedTab = parseScorecardTabParam(searchParams.get('tab'));
  const firstTabId = scorecardTabs[0]?.id ?? null;
  const selectedTab = useMemo(() => {
    if (scorecardTabs.length === 0) {
      return requestedTab;
    }
    if (requestedTab && scorecardTabs.some((tab) => tab.id === requestedTab)) {
      return requestedTab;
    }
    return firstTabId;
  }, [firstTabId, requestedTab, scorecardTabs]);

  const baseSections = useMemo(
    () => buildScorecardSectionsFromApi(scorecardResponse?.metrics, scorecardTabs),
    [scorecardResponse?.metrics, scorecardTabs]
  );

  // The two value columns are snapshot-over-snapshot: the selected snapshot and
  // the immediately previous snapshot (snapshot_periods = [selected, prior]).
  const snapshotColumnPeriods = useMemo(
    () => scorecardResponse?.snapshot_periods ?? [],
    [scorecardResponse?.snapshot_periods]
  );
  const currentSnapshotPeriod = snapshotColumnPeriods[0];
  const priorSnapshotPeriod = snapshotColumnPeriods[1];
  const currentDate = parseApiDate(currentSnapshotPeriod);
  const priorDate = parseApiDate(priorSnapshotPeriod);
  const currentQuarterLabel = formatQuarterLabel(
    currentSnapshotPeriod === SCORECARD_CURRENT_SNAPSHOT_DT ? new Date() : currentDate
  );
  // End-of-quarter snapshots show the quarter's actual instead of a snapshot
  // value, and are never compared against a prior snapshot.
  const currentDateLabel = isEndOfQuarter
    ? `${currentQuarterLabel} Actual`
    : currentSnapshotPeriod === SCORECARD_CURRENT_SNAPSHOT_DT
      ? 'Current'
      : currentDate
        ? formatSnapshotLabel(currentDate)
        : '';
  const priorDateLabel = isEndOfQuarter
    ? null
    : currentSnapshotPeriod === SCORECARD_CURRENT_SNAPSHOT_DT
      ? priorDate
        ? 'Previous'
        : null
      : priorDate
        ? formatSnapshotLabel(priorDate)
        : null;

  const handleTabSelection = useCallback(
    (tabId: string) => {
      const tab = scorecardTabs.find((scorecardTab) => scorecardTab.id === tabId);
      if (!tab) {
        return;
      }

      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (tabId === firstTabId) {
            next.delete('tab');
          } else {
            next.set('tab', buildScorecardTabParam(tab));
          }
          return next;
        },
        {replace: true}
      );
    },
    [firstTabId, setSearchParams, scorecardTabs]
  );

  const selectedSection = useMemo(
    () => baseSections.find((section) => section.id === selectedTab),
    [baseSections, selectedTab]
  );

  const sections = useMemo(
    () => getScorecardSections(selectedTab, baseSections),
    [baseSections, selectedTab]
  );
  const showSectionHeaders = selectedSection?.grouped ?? false;

  // Snapshot label for the stepper = the capture date, or "End of Qx".
  const snapshotDate = parseApiDate(selectedSnapshotDt);
  const snapshotLabel =
    selectedSnapshotDt === SCORECARD_CURRENT_SNAPSHOT_DT
      ? 'Current'
      : snapshotDate
        ? isEndOfQuarter
          ? formatEndOfQuarterLabel(snapshotDate)
          : formatSnapshotLabel(snapshotDate)
        : '';

  const canGoOlder = snapshotIndex >= 0 && snapshotIndex < snapshotOptions.length - 1;
  const canGoNewer = snapshotIndex > 0;

  const selectedScorecardTab = selectedTab ?? firstTabId ?? '';

  // Per-pillar snapshot summaries persisted server-side, keyed by tab_id.
  const snapshotSummaries = useMemo(
    () => scorecardResponse?.snapshot_summaries ?? {},
    [scorecardResponse?.snapshot_summaries]
  );

  if (isError) {
    return (
      <Layout.Container width="100%">
        <Layout.Header showScrollShadow={false}>
          <Title size={24}>{t('scorecards')}</Title>
        </Layout.Header>
        <EmptyState
          title="Could not load scorecards"
          body={
            error instanceof Error ? error.message : 'Something went wrong. Please try again later.'
          }
          image={<LaptopMini width={64} altText="" />}
          hideBorder
        />
      </Layout.Container>
    );
  }

  const hasAnyMetrics = baseSections.some((s) => s.metrics.length > 0);
  const hasUpdateCards = Boolean(scorecardResponse?.workstream_update_cards?.length);
  const isScorecardsUnavailable =
    !isLoading && snapshots.length === 0 && !hasAnyMetrics && !hasUpdateCards;
  // Only the selected snapshot is empty (e.g. a period before any data exists).
  // Keep the stepper rendered so the user can navigate back to a populated one.
  const isSelectedWindowEmpty = !isLoading && snapshots.length > 0 && !hasAnyMetrics;

  if (isScorecardsUnavailable) {
    return (
      <Layout.Container width="100%">
        <Layout.Header showScrollShadow={false}>
          <Title size={24}>{t('scorecards')}</Title>
        </Layout.Header>
        <EmptyState
          title={t('scorecard_none_title')}
          body={t('scorecard_none_body')}
          image={<LaptopMini width={64} altText="" />}
          hideBorder
        />
      </Layout.Container>
    );
  }

  return (
    <Layout.Container width="100%">
      <Layout.Header showScrollShadow={false}>
        <Stack gap="16" width="100%">
          <Split gap="16" alignY="center">
            <Split.Item>
              <Title size={24}>{t('scorecards')}</Title>
            </Split.Item>
            <Split.Item width="hug">
              <Box display="flex" alignItems="center" paddingX="8" style={{gap: 16}}>
                <IconButton
                  aria-label="Previous snapshot"
                  variant={canGoOlder ? 'filled' : 'opacity'}
                  size="small"
                  disabled={!canGoOlder}
                  className={canGoOlder ? undefined : atoms({color: 'Disabled On Surface'})}
                  onClick={() => handleSnapshotNavigation(snapshotIndex + 1)}
                >
                  <UIIcon src={ChevronLeftLine} />
                </IconButton>
                <Box paddingX="4" style={{minWidth: 60, textAlign: 'center'}}>
                  <Text size="medium" isBold>
                    {snapshotLabel || '--'}
                  </Text>
                </Box>
                <IconButton
                  aria-label="Next snapshot"
                  variant={canGoNewer ? 'filled' : 'opacity'}
                  size="small"
                  disabled={!canGoNewer}
                  className={canGoNewer ? undefined : atoms({color: 'Disabled On Surface'})}
                  onClick={() => handleSnapshotNavigation(Math.max(0, snapshotIndex - 1))}
                >
                  <UIIcon src={ChevronRightLine} />
                </IconButton>
              </Box>
            </Split.Item>
            <Split.Item width="fill" />
            <Split.Item width="hug">
              <ScorecardQuarterCloseSelector
                endOfQuarterSnapshots={endOfQuarterSnapshots}
                selectedSnapshotDt={selectedSnapshotDt}
                onSelect={(snapshotDt) => navigate(getScorecardSnapshotPath(snapshotDt))}
              />
            </Split.Item>
          </Split>

          <Tabs selectedTab={selectedTab ?? firstTabId ?? ''} onSelection={handleTabSelection}>
            <Tabs.Group>
              {scorecardTabs.map((tab) => (
                <Tabs.Tab key={tab.id} id={tab.id}>
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.Group>
          </Tabs>
        </Stack>
      </Layout.Header>

      {isSelectedWindowEmpty ? (
        <EmptyState
          title={t('scorecard_period_empty_title')}
          body={t('scorecard_period_empty_body')}
          image={<LaptopMini width={64} altText="" />}
          hideBorder
        />
      ) : (
        <>
          {SHOW_SCORECARD_SNAPSHOT_SUMMARIES && selectedScorecardTab ? (
            <ScorecardSnapshotSummaryEditor
              key={`${selectedSnapshotDt ?? 'latest'}:${selectedScorecardTab}`}
              snapshotDt={selectedSnapshotDt}
              tabId={selectedScorecardTab}
              snapshotLabel={snapshotLabel}
              initialSummary={snapshotSummaries[selectedScorecardTab] ?? ''}
            />
          ) : null}

          <ScorecardsTable
            sections={sections}
            showSectionHeaders={showSectionHeaders}
            snapshotIndex={0}
            snapshotKey={selectedSnapshotDt}
            currentDateLabel={currentDateLabel}
            priorDateLabel={priorDateLabel}
            currentQuarterLabel={currentQuarterLabel}
            isEndOfQuarter={isEndOfQuarter}
            loading={isLoading}
          />
        </>
      )}
      {!isLoading && (
        <ScorecardWorkstreamUpdateCards
          cards={scorecardResponse?.workstream_update_cards ?? []}
          snapshotDt={selectedSnapshotDt}
        />
      )}
    </Layout.Container>
  );
};
